<template>
  <div
    v-if="visible && currentAlert"
    id="connection-alert-snackbar"
    class="connection-alert-snackbar connection-alert-snackbar--visible"
    role="status"
    aria-live="polite"
    aria-hidden="false"
  >
    <div class="connection-alert-snackbar__panel">
      <button
        type="button"
        class="connection-alert-snackbar__close"
        :title="$t('ConnectionAlerts.CloseAria')"
        :aria-label="$t('ConnectionAlerts.CloseAria')"
        @click="dismissFor24h"
      >
        &times;
      </button>
      <h2 class="connection-alert-snackbar__title">{{ alertTitle }}</h2>
      <p class="connection-alert-snackbar__text">{{ alertBody }}</p>
      <div class="connection-alert-snackbar__actions">
        <a
          class="connection-alert-snackbar__btn connection-alert-snackbar__btn--primary"
          :href="reconnectHref"
          target="_self"
        >
          {{ $t("ConnectionAlerts.Reconnect") }}
        </a>
        <button
          type="button"
          class="connection-alert-snackbar__btn connection-alert-snackbar__btn--secondary"
          @click="ignoreFor3Years"
        >
          {{ $t("ConnectionAlerts.Ignore3y") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import axios from "axios";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import ConfigService from "@/services/ConfigService";
import { requestConfig } from "@/services/api/ApiService";
import { UserData } from "@/models/UserData";

const namespace = "mainModule";

interface ConnectionAlertDto {
  Provider?: string;
  CookieTokenKey?: number;
  ReconnectUrl?: string;
  Title?: string;
  Body?: string;
}

const maxAge24h = 86400;
const maxAge3y = 3 * 365 * 86400;

@Component
export default class ConnectionAlertsPanel extends Vue {
  @Getter("userData", { namespace }) userData!: UserData;

  private queue: ConnectionAlertDto[] = [];
  visible = false;

  get currentAlert(): ConnectionAlertDto | null {
    return this.queue.length ? this.queue[0] : null;
  }

  get reconnectHref(): string {
    const a = this.currentAlert;
    if (!a) {
      return "javascript:void(0)";
    }
    const u = a.ReconnectUrl ?? "";
    return u || "javascript:void(0)";
  }

  get alertTitle(): string {
    const a = this.currentAlert;
    if (!a) {
      return "";
    }
    return (a.Title ?? "").trim();
  }

  get alertBody(): string {
    const a = this.currentAlert;
    if (!a) {
      return "";
    }
    return (a.Body ?? "").trim();
  }

  @Watch("userData", { immediate: true })
  onUserData(userData: UserData | null | undefined): void {
    if (!userData?.Id) {
      return;
    }
    void this.fetchAlerts();
  }

  private alertProvider(a: ConnectionAlertDto): string {
    return a.Provider ?? "";
  }

  private alertCookieTokenKey(a: ConnectionAlertDto): number {
    return a.CookieTokenKey ?? 0;
  }

  private cookieName(item: ConnectionAlertDto): string {
    return `fix_${this.alertProvider(item)}_token_notvalid_${this.alertCookieTokenKey(item)}`;
  }

  private cookieExists(name: string): boolean {
    return document.cookie.split(";").some((c) => c.trim().indexOf(name + "=") === 0);
  }

  private setCookie(name: string, maxAge: number): void {
    document.cookie = `${name}=1; max-age=${maxAge}; path=/; SameSite=Lax`;
  }

  private async fetchAlerts(): Promise<void> {
    try {
      const response = await axios.get<{ alerts?: ConnectionAlertDto[] }>(
        ConfigService.connectionAlertsActiveUrl,
        requestConfig
      );
      const raw = response.data?.alerts;
      if (!raw || !Array.isArray(raw)) {
        this.queue = [];
        this.visible = false;
        return;
      }
      this.queue = raw.filter((a) => !this.cookieExists(this.cookieName(a)));
      this.visible = this.queue.length > 0;
    } catch {
      this.queue = [];
      this.visible = false;
    }
  }

  private popCurrent(): void {
    this.queue = this.queue.slice(1);
    this.visible = this.queue.length > 0;
  }

  dismissFor24h(): void {
    const cur = this.currentAlert;
    if (!cur) {
      return;
    }
    this.setCookie(this.cookieName(cur), maxAge24h);
    this.popCurrent();
  }

  ignoreFor3Years(): void {
    const cur = this.currentAlert;
    if (!cur) {
      return;
    }
    this.setCookie(this.cookieName(cur), maxAge3y);
    this.popCurrent();
  }

}
</script>
